import { Grid } from "@mui/material";
import { debounce } from "lodash";
import React, { useCallback, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import GlobalContext from "../../../../framework/GlobalContext";
import NumberInput from "../../../../framework/NumberInput";
import TextInput from "../../../../framework/TextInput";
import { filterBanks } from "../../../../services/bank-services";
import { gstTypes } from "../../../../utils/constant";
import {
  checkErrorStateExists,
  formStateHandler,
} from "../../../../utils/utils";

const GeneralTab = (props) => {
  const {
    removeErrorStateOnChange = () => {},
    validateForm = () => {},
    editBankId = null,
    branches,
  } = props;

  const dispatch = useDispatch();

  const { setCurrentComponent } = useContext(GlobalContext);
  const withAccountNoCap = ({ value }) => value.length <= 25;
  const { formState, errorState } = useSelector(
    (state) => state.commonReducer.common.addBank || {}
  );

  const dispatchFormState = useCallback(
    (newState) => {
      dispatch({
        type: "set_add_bank_form_state",
        payload: newState,
      });
    },
    [dispatch]
  );

  const dispatchSelectedBankFormState = useCallback(
    (data) => {
      const selectedgstinType = gstTypes.find(
        (x) => x.label === data?.gstinType
      );
      let _branches = [];
      if (data?.branchCode && data?.branchCode?.length) {
        let updated = [];
        data?.branchCode?.forEach((b) => {
          const _b = branches.find((x) => x.code === b);
          if (_b) updated = [...updated, _b];
        });
        _branches = updated;
      }
      const payload = {
        bankId: data?.bankId,
        bankName: data?.bankName,
        shortName: data?.shortName,
        bankCode: data?.bankCode,
        bankEmail: data?.bankEmail,
        accountNo: data?.accountNo,
        micrCode: data?.micrCode,
        ifsc: data?.ifsc,
        discription: data?.discription,
        note: data?.note,
        gstin: data?.gstin,
        provisionalGstin: data?.provisionalGstin,
        bankAddress: data?.bankAddress,
        locality: data?.locality,
        pincode: data?.pincode,
        city: data?.city,
        area: data?.area,
        region: data?.region,
        country: data?.country,
        zone: data?.zone,
        branchCode: _branches,
        gstinType: selectedgstinType,
      };

      dispatch({
        type: "set_add_bank_form_state",
        payload: payload,
      });
    },
    [branches, dispatch]
  );

  const updateSelectedBank = useCallback(
    (bankId) => {
      if (!bankId) return;
      filterBanks(`field=bankId&value=${bankId}`).then((res) => {
        if (!res) return;
        const banks = res.rows;
        if (banks.length === 1) {
          dispatchSelectedBankFormState(banks[0]);
        }
        return;
      });
    },
    [dispatchSelectedBankFormState]
  );

  useEffect(() => {
    if (!editBankId) return;
    updateSelectedBank(editBankId);
  }, [updateSelectedBank, editBankId]);

  const debounceDispatchFormState = useCallback(
    debounce(dispatchFormState, 1),
    [dispatchFormState]
  );

  const handleFormChange = useCallback(
    (fieldName, fieldValue, isSelect = false, options) => {
      let newFormState = formStateHandler(
        formState,
        fieldName,
        fieldValue,
        isSelect,
        options
      );
      debounceDispatchFormState(newFormState);
    },
    [debounceDispatchFormState, formState]
  );

  return (
    <>
      <Grid
        container
        spacing={2}
        columns={6}
        sx={{
          margin: "5px 0px 5px -10px",
          width: "100%",
        }}
      >
        <Grid item xs={1.8}>
          <TextInput
            label="Bank Name"
            id="add_bank_name"
            value={formState?.bankName}
            onFocus={() => {
              setCurrentComponent("add_bank_name");
            }}
            onBlur={(e) => {
              validateForm("add_bank_name", formState);
            }}
            onChange={(e, value) => {
              const sanitizedValue = e.target.value.replace(
                /[^a-zA-Z0-9\s]/g,
                ""
              );
              handleFormChange("bankName", sanitizedValue);
              removeErrorStateOnChange("add_bank_name");
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                down: "add_bank_acc_no",
                right: "add_bank_short_name",
                up: "add_bank_name",
                left: "add_bank_name",
              }),
            }}
            error={checkErrorStateExists("add_bank_name", errorState)}
            required
            autoFocus
          />
        </Grid>

        <Grid item xs={1.8}>
          <TextInput
            label="Short Name"
            id="add_bank_short_name"
            value={formState?.shortName}
            onFocus={() => {
              setCurrentComponent("add_bank_short_name");
            }}
            onBlur={(e) => {
              validateForm("add_bank_short_name", formState);
            }}
            onChange={(e, value) => {
              handleFormChange("shortName", e.target.value);
              removeErrorStateOnChange("add_bank_short_name");
            }}
            inputProps={{
              maxLength: 50,
              "data-markers": JSON.stringify({
                down: "add_bank_micr_code",
                left: "add_bank_name",
                right: "add_bank_code",
                up: "add_bank_name",
              }),
            }}
            error={checkErrorStateExists("add_bank_short_name", errorState)}
          />
        </Grid>

        <Grid item xs={1.8}>
          <TextInput
            label="Bank Code"
            id="add_bank_code"
            value={formState?.bankCode}
            onFocus={() => {
              setCurrentComponent("add_bank_code");
            }}
            onBlur={(e) => {
              validateForm("add_bank_code", formState);
            }}
            onChange={(e, value) => {
              const sanitizedBankCode = e.target.value.replace(
                /[^a-zA-Z0-9]/g,
                ""
              );
              handleFormChange("bankCode", sanitizedBankCode);
              removeErrorStateOnChange("add_bank_code");
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                down: "add_bank_ifsc_code",
                left: "add_bank_short_name",
                right: "add_bank_acc_no",
                up: "add_bank_short_name",
              }),
            }}
            error={checkErrorStateExists("add_bank_code", errorState)}
          />
        </Grid>

        <Grid item xs={1.8}>
          <NumberInput
            label="A/C No."
            id="add_bank_acc_no"
            value={formState?.accountNo}
            isAllowed={withAccountNoCap}
            allowNegative={false}
            onFocus={() => {
              setCurrentComponent("add_bank_acc_no");
            }}
            onBlur={(e) => {
              validateForm("add_bank_acc_no", formState);
            }}
            onChange={(e, value) => {
              handleFormChange("accountNo", e.target.value);
              removeErrorStateOnChange("add_bank_acc_no");
            }}
            decimalScale={0}
            inputProps={{
              "data-markers": JSON.stringify({
                down: "add_bank_micr_code",
                left: "add_bank_code",
                right: "add_bank_micr_code",
                up: "add_bank_name",
              }),
            }}
            error={checkErrorStateExists("add_bank_acc_no", errorState)}
          />
        </Grid>

        <Grid item xs={1.8}>
          <TextInput
            label="MICR CODE"
            id="add_bank_micr_code"
            value={formState?.micrCode}
            onFocus={() => {
              setCurrentComponent("add_bank_micr_code");
            }}
            onBlur={(e) => {
              validateForm("add_bank_micr_code", formState);
            }}
            onChange={(e, value) => {
              const sanitizedMICRCode = e.target.value.replace(
                /[^a-zA-Z0-9]/g,
                ""
              );
              handleFormChange("micrCode", sanitizedMICRCode);
              removeErrorStateOnChange("add_bank_micr_code");
            }}
            inputProps={{
              maxLength: 25,
              "data-markers": JSON.stringify({
                down: "add_bank_ifsc_code",
                left: "add_bank_acc_no",
                right: "add_bank_ifsc_code",
                up: "add_bank_short_name",
              }),
            }}
            error={checkErrorStateExists("add_bank_micr_code", errorState)}
          />
        </Grid>

        <Grid item xs={1.8}>
          <TextInput
            label="IFSC CODE"
            id="add_bank_ifsc_code"
            value={formState?.ifsc}
            onFocus={() => {
              setCurrentComponent("add_bank_ifsc_code");
            }}
            onBlur={(e) => {
              validateForm("add_bank_ifsc_code", formState);
            }}
            onChange={(e, value) => {
              const ifsc = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
              handleFormChange("ifsc", ifsc);
              removeErrorStateOnChange("add_bank_ifsc_code");
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                down: "add_bank_desc",
                left: "add_bank_micr_code",
                right: "add_bank_desc",
                up: "add_bank_code",
              }),
            }}
            error={checkErrorStateExists("add_bank_ifsc_code", errorState)}
          />
        </Grid>

        <Grid item xs={12}>
          <TextInput
            label="Description"
            id="add_bank_desc"
            value={formState?.discription}
            onFocus={() => {
              setCurrentComponent("add_bank_desc");
            }}
            onBlur={(e) => {
              validateForm("add_bank_desc", formState);
            }}
            onChange={(e, value) => {
              handleFormChange("discription", e.target.value);
              removeErrorStateOnChange("add_bank_desc");
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                down: "add_bank_note",
                left: "add_bank_ifsc_code",
                right: "add_bank_note",
                up: "add_bank_ifsc_code",
              }),
            }}
            error={checkErrorStateExists("add_bank_desc", errorState)}
          />
        </Grid>

        <Grid item xs={12}>
          <TextInput
            label="Note"
            id="add_bank_note"
            value={formState?.note}
            onFocus={() => {
              setCurrentComponent("add_bank_note");
            }}
            onBlur={(e) => {
              validateForm("add_bank_note", formState);
            }}
            onChange={(e, value) => {
              handleFormChange("note", e.target.value);
              removeErrorStateOnChange("add_bank_note");
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                down: "add_bank_name",
                left: "add_bank_desc",
                right: "add_bank_name",
                up: "add_bank_desc",
              }),
            }}
            error={checkErrorStateExists("add_bank_note", errorState)}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default GeneralTab;
